import { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import axios from 'axios';

const hasFileUpload = (obj) => {
  if (!obj || typeof obj !== 'object') return false;
  if (typeof File !== 'undefined' && obj instanceof File) return true;
  for (const val of Object.values(obj)) {
    if (typeof File !== 'undefined' && val instanceof File) return true;
    if (Array.isArray(val) && val.some((item) => hasFileUpload(item))) return true;
  }
  return false;
};

export const SAVED_DISPLAY_MS = 1000;

const buildFormData = (formData, data, parentKey) => {
  if (data === null || data === undefined) return;
  if (typeof File !== 'undefined' && data instanceof File) {
    formData.append(parentKey, data);
  } else if (Array.isArray(data)) {
    data.forEach((item, index) => {
      buildFormData(formData, item, `${parentKey}[${index}]`);
    });
  } else if (typeof data === 'object' && !(data instanceof Date)) {
    Object.entries(data).forEach(([key, value]) => {
      buildFormData(formData, value, parentKey ? `${parentKey}[${key}]` : key);
    });
  } else {
    formData.append(parentKey, data);
  }
};

export default function useAutoSave({
  team,
  csrfToken,
  resourcePath,
  resourceKey,
  existingId = null,
  isOpen = true,
  onSaved,
}) {
  const [recordId, setRecordId] = useState(existingId);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [errors, setErrors] = useState([]);
  const debounceTimerRef = useRef(null);
  const afterSaveTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const sessionRef = useRef(0);
  const submittingRef = useRef(false);

  useLayoutEffect(() => {
    if (!isOpen) {
      sessionRef.current += 1;
      submittingRef.current = false;
      clearTimeout(afterSaveTimerRef.current);
      return;
    }
    setRecordId(existingId);
    setSaveStatus('idle');
    setErrors([]);
  }, [existingId, isOpen]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      clearTimeout(afterSaveTimerRef.current);
    };
  }, []);

  const persistFields = useCallback(async (fields, { afterSave } = {}) => {
    if (!team || !team.id) return;
    const session = sessionRef.current;
    const isCurrentSession = () => isMountedRef.current && sessionRef.current === session;

    try {
      setSaveStatus('saving');
      setErrors([]);

      if (csrfToken) {
        axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      }

      const basePath = `/teams/${team.id}/${resourcePath}`;
      let response;
      let isNew = false;
      let payload;

      if (hasFileUpload(fields)) {
        payload = new FormData();
        buildFormData(payload, fields, resourceKey);
      } else {
        payload = { [resourceKey]: fields };
      }

      if (recordId) {
        response = await axios.patch(`${basePath}/${recordId}.json`, payload);
      } else {
        response = await axios.post(`${basePath}.json`, payload);
        isNew = true;
      }

      const saved = response.data;

      if (!isMountedRef.current) return;
      if (isNew && saved.id && isCurrentSession()) setRecordId(saved.id);
      let result;
      try {
        result = onSaved ? await onSaved(saved, isNew) : undefined;
      } catch (onSavedError) {
        result = false;
      }
      if (!isCurrentSession()) return;
      setSaveStatus('saved');
      if (afterSave && result !== false) {
        afterSaveTimerRef.current = setTimeout(() => {
          afterSaveTimerRef.current = null;
          if (isCurrentSession()) afterSave(saved, isNew);
        }, SAVED_DISPLAY_MS);
        return;
      }
      if (afterSave) submittingRef.current = false;
    } catch (e) {
      if (!isCurrentSession()) return;
      if (afterSave) submittingRef.current = false;
      const data = e.response && e.response.data;
      if (Array.isArray(data)) {
        setErrors(data);
      } else if (typeof data === 'string') {
        setErrors([data]);
      } else {
        setErrors(['Failed to save. Please try again.']);
      }
      setSaveStatus('error');
    }
  }, [team, csrfToken, resourcePath, resourceKey, recordId, onSaved]);

  const saveFields = useCallback((fields, options = {}) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (!team || !team.id) return;
    if (options.afterSave) {
      if (afterSaveTimerRef.current) {
        clearTimeout(afterSaveTimerRef.current);
        afterSaveTimerRef.current = null;
        submittingRef.current = false;
      }
      if (submittingRef.current) return;
      submittingRef.current = true;
    }
    persistFields(fields, options);
  }, [team, persistFields]);

  const debouncedSaveFields = useCallback((fields, delay = 800) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      persistFields(fields);
    }, delay);
  }, [persistFields]);

  return {
    recordId,
    saveStatus,
    errors,
    saveFields,
    debouncedSaveFields,
  };
}
